import { AspectRatio } from '../database/custom-types/aspect-ratio'

/**
 * Root playlist `aspectRatio` is not stored on persisted JSON; it is derived from the first frame’s
 * layout canvas dimensions (`width` × `height`).
 */
interface PlaylistFrameLayoutInput {
  layout: { width: number, height: number }
  rssDirectoryRef?: unknown
}

/** Frames that define playlist canvas orientation (excludes responsive RSS directory frames). */
export function playlistContentFramesForAspect<T extends PlaylistFrameLayoutInput>(
  frames: ReadonlyArray<T>
): T[] {
  return frames.filter(f => f.rssDirectoryRef == null)
}

/** Coarse canvas orientation for UI / registration, from the first frame’s layout size. */
export function aspectRatioFromPlaylistFrameLayouts(
  frames: ReadonlyArray<{ layout: { width: number, height: number } }>
): AspectRatio {
  const first = frames[0]
  if (first == null) {
    return AspectRatio.Horizontal
  }
  const w = first.layout.width
  const h = first.layout.height
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return AspectRatio.Horizontal
  }
  if (w === h) {
    return AspectRatio.Square
  }
  return w > h ? AspectRatio.Horizontal : AspectRatio.Vertical
}

/** Like {@link aspectRatioFromPlaylistFrameLayouts} but ignores RSS directory frames. */
export function aspectRatioFromPlaylistContentFrameLayouts(
  frames: ReadonlyArray<PlaylistFrameLayoutInput>
): AspectRatio {
  return aspectRatioFromPlaylistFrameLayouts(playlistContentFramesForAspect(frames))
}

/** Same as {@link aspectRatioFromPlaylistFrameLayouts}; reads `playlist.frames`. */
export function playlistCanvasAspectRatio(playlist: { frames: ReadonlyArray<{ layout: { width: number, height: number } }> }): AspectRatio {
  return aspectRatioFromPlaylistFrameLayouts(playlist.frames)
}

/** Same as {@link playlistCanvasAspectRatio} but ignores RSS directory frames. */
export function playlistContentCanvasAspectRatio(
  playlist: { frames: ReadonlyArray<PlaylistFrameLayoutInput> }
): AspectRatio {
  return aspectRatioFromPlaylistContentFrameLayouts(playlist.frames)
}

/** Viewport / window aspect classification (same rules as the first frame of a playlist). */
export function aspectRatioFromViewportDimensions(width: number, height: number): AspectRatio {
  return aspectRatioFromPlaylistFrameLayouts([{ layout: { width, height } }])
}

/** Coarse aspect bucket for one frame layout (from canvas size, not the stored `layout.aspectRatio` tag). */
export function frameLayoutAspectBucket(frame: { layout: { width: number, height: number } }): AspectRatio {
  return aspectRatioFromPlaylistFrameLayouts([frame])
}
